import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle2, Loader2, RotateCw } from "lucide-react";
import { cn } from "@/lib/cn";
import { Skeleton } from "@/components/ui/skeleton";
import type { NotificationSeverity } from "@/api/notifications";
import { SEVERITY_META, SOURCE_FILTERS } from "@/lib/notificationMeta";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationCenter,
} from "@/hooks/useNotifications";
import { NotificationRow } from "./NotificationRow";

const PAGE_SIZE = 20;

const SEVERITY_CHIPS: NotificationSeverity[] = ["critical", "warning", "info", "reminder"];

/**
 * D-043 S2 — Центр сповіщень (/notifications), TZ §6.2 + mockup екран 2/2b.
 * Read-state мутації лише explicit (mark-read / mark-all) — фетч не маркує.
 */
export function NotificationCenter() {
  const navigate = useNavigate();
  const [view, setView] = useState<"all" | "unread">("all");
  const [severity, setSeverity] = useState<NotificationSeverity[]>([]);
  const [source, setSource] = useState<string[]>([]);
  const [offset, setOffset] = useState(0);

  const filters = useMemo(
    () => ({ view, severity, source, offset, limit: PAGE_SIZE }),
    [view, severity, source, offset],
  );

  const { data, isPending, isError, refetch, isRefetching, isPlaceholderData } =
    useNotificationCenter(filters);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const toggleSeverity = (s: NotificationSeverity) => {
    setOffset(0);
    setSeverity((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const toggleSource = (s: string) => {
    setOffset(0);
    setSource((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const total = data?.total ?? 0;
  const unreadTotal = data?.unreadTotal ?? 0;
  const tabTotal = view === "all" ? total : unreadTotal;
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < tabTotal;
  const noFilters = severity.length === 0 && source.length === 0;

  return (
    <div className="mx-auto w-full max-w-[1080px] p-4 md:p-6">
      <div className="overflow-hidden rounded-card border border-border bg-card shadow-sm">
        {/* header */}
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <Bell className="h-[22px] w-[22px] text-primary" aria-hidden="true" />
          <h1 className="text-lg font-bold tracking-tight text-foreground">Центр сповіщень</h1>
          <div className="flex-1" />
          {unreadTotal > 0 && (
            <button
              type="button"
              onClick={() => markAll.mutate()}
              disabled={isPending || markAll.isPending}
              title="Позначити прочитаними не закриває задачі, запити чи погодження"
              className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
            >
              {markAll.isPending && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}
              Позначити всі прочитаними
            </button>
          )}
        </div>

        {/* tabs */}
        <div className="flex gap-1 border-b border-border px-5 py-2.5" role="tablist">
          {(
            [
              { k: "all", label: "Усі", n: total },
              { k: "unread", label: "Непрочитані", n: unreadTotal },
            ] as const
          ).map(({ k, label, n }) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={view === k}
              onClick={() => {
                setView(k);
                setOffset(0);
              }}
              className={cn(
                "rounded-badge px-3 py-1.5 text-[13px] font-semibold transition-colors",
                view === k
                  ? "bg-success text-success-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground",
              )}
            >
              {label} <span className="tabular-nums opacity-60">·{n}</span>
            </button>
          ))}
        </div>

        {/* filters */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-2.5">
          <span className="text-xs font-semibold text-muted-foreground/70">Важливість:</span>
          <FilterChip active={severity.length === 0} onClick={() => { setSeverity([]); setOffset(0); }}>
            Усі
          </FilterChip>
          {SEVERITY_CHIPS.map((s) => (
            <FilterChip key={s} active={severity.includes(s)} onClick={() => toggleSeverity(s)}>
              {SEVERITY_META[s].label}
            </FilterChip>
          ))}
          <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
          <span className="text-xs font-semibold text-muted-foreground/70">Джерело:</span>
          {SOURCE_FILTERS.map(({ value, label }) => (
            <FilterChip key={value} active={source.includes(value)} onClick={() => toggleSource(value)}>
              {label}
            </FilterChip>
          ))}
        </div>

        {/* body */}
        {isPending && (
          <div className="space-y-4 p-5" aria-label="Завантаження">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-9 w-9 rounded-badge" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-2/5" />
                </div>
              </div>
            ))}
          </div>
        )}

        {isError && !isPending && (
          <div className="px-5 py-12 text-center">
            <p className="mb-4 text-sm text-muted-foreground">Не вдалося завантажити</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="inline-flex items-center gap-1.5 rounded-badge border border-primary px-4 py-2 text-xs font-semibold text-primary hover:bg-primary/5"
            >
              <RotateCw className={cn("h-3.5 w-3.5", isRefetching && "animate-spin")} />
              Повторити
            </button>
          </div>
        )}

        {data && !isError && data.list.length === 0 && (
          <div className="px-5 py-12 text-center">
            <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-card bg-success text-success-foreground">
              <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
            </div>
            <div className="mb-1 font-semibold text-foreground">
              {noFilters ? "Усе під контролем" : "Нічого не знайдено"}
            </div>
            <p className="mb-4 text-sm text-muted-foreground">
              {noFilters && view === "all"
                ? "Немає сповіщень. Нові зʼявляться тут і в дзвіночку."
                : view === "unread" && noFilters
                  ? "Немає непрочитаних сповіщень. Нові зʼявляться тут і в дзвіночку."
                  : "За цими фільтрами сповіщень немає."}
            </p>
            {noFilters ? (
              <button
                type="button"
                onClick={() => navigate("/today")}
                className="rounded-badge border border-primary px-4 py-2 text-xs font-semibold text-primary hover:bg-primary/5"
              >
                Перейти до робочого списку
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setSeverity([]);
                  setSource([]);
                  setOffset(0);
                }}
                className="rounded-badge border border-border px-4 py-2 text-xs font-semibold text-muted-foreground hover:bg-accent"
              >
                Скинути фільтри
              </button>
            )}
          </div>
        )}

        {data && !isError && data.list.length > 0 && (
          <div role="list" className={cn(isPlaceholderData && "opacity-60")}>
            {data.list.map((row) => (
              <NotificationRow
                key={row.id}
                row={row}
                size="lg"
                onMarkRead={(id) => markRead.mutate(id)}
              />
            ))}
          </div>
        )}

        {/* pagination */}
        {data && (hasPrev || hasNext) && (
          <div className="flex items-center justify-between border-t border-border px-5 py-3">
            <button
              type="button"
              disabled={!hasPrev}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="rounded-badge border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-accent disabled:opacity-40"
            >
              ← Новіші
            </button>
            <span className="text-xs tabular-nums text-muted-foreground/70">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, tabTotal)} з {tabTotal}
            </span>
            <button
              type="button"
              disabled={!hasNext}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="rounded-badge border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-accent disabled:opacity-40"
            >
              Давніші →
            </button>
          </div>
        )}
      </div>

      <p className="mt-3 px-1 text-xs text-muted-foreground/70">
        Позначити прочитаними не закриває задачі, запити чи погодження.
      </p>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "rounded-badge border px-2.5 py-1 text-xs font-semibold transition-colors",
        active
          ? "border-primary bg-success text-primary"
          : "border-border bg-card text-muted-foreground hover:bg-accent",
      )}
    >
      {children}
    </button>
  );
}
